/**
 * EmailConfirmedPage - Shown after clicking the email confirmation link
 * SESSION_189: Email confirmation enforcement
 *
 * Handles both success and error states based on query parameters:
 * - ?success=true -> Show success message and redirect to dashboard
 * - ?success=false&error=xxx -> Show error message
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  Button,
  Stack,
  Alert,
  ThemeIcon,
  Loader
} from '@mantine/core';
import { IconCheck, IconAlertCircle, IconLogin } from '@tabler/icons-react';
import { useAuthStore, updateEmailConfirmedStatus } from '../../../application/store/authStore';
import { CUSTOMER_ROUTES, NONPROFIT_ROUTES, PUBLIC_ROUTES } from '../../routing/routes';

type ErrorCode = 'invalid-link' | 'token-expired' | 'invalid-token' | 'server-error';

const errorMessages: Record<ErrorCode, string> = {
  'invalid-link': 'The confirmation link is invalid. Please request a new confirmation email.',
  'token-expired': 'The confirmation link has expired. Please request a new confirmation email.',
  'invalid-token': 'The confirmation token is invalid. Please request a new confirmation email.',
  'server-error': 'An error occurred while confirming your email. Please try again later.'
};

export const EmailConfirmedPage: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const userRole = useAuthStore((state) => state.user?.role);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const dashboardRoute = userRole === 'NonProfitUser'
    ? NONPROFIT_ROUTES.DASHBOARD
    : CUSTOMER_ROUTES.DASHBOARD;

  const success = searchParams.get('success') === 'true';
  const errorCode = searchParams.get('error') as ErrorCode | null;

  const errorMessage = errorCode && errorCode in errorMessages
    ? errorMessages[errorCode]
    : errorCode
      ? 'An unknown error occurred. Please try again.'
      : null;

  // Update auth store and redirect on success
  useEffect(() => {
    if (success && isAuthenticated) {
      // Update the auth store to reflect email confirmed
      updateEmailConfirmedStatus(true);

      // Auto-redirect to dashboard after 3 seconds
      setIsRedirecting(true);
      const timer = setTimeout(() => {
        navigate(dashboardRoute, { replace: true });
      }, 3000);

      return (): void => {
        clearTimeout(timer);
      };
    }
    return undefined;
  }, [success, isAuthenticated, navigate]);

  const handleContinue = useCallback((): void => {
    if (isAuthenticated) {
      navigate(CUSTOMER_ROUTES.DASHBOARD, { replace: true });
    } else {
      // Not logged in - redirect to login
      window.location.href = '/login';
    }
  }, [isAuthenticated, navigate]);

  const handleTryAgain = useCallback((): void => {
    if (isAuthenticated) {
      navigate(PUBLIC_ROUTES.CONFIRM_EMAIL_PENDING, { replace: true });
    } else {
      // Not logged in - redirect to login
      window.location.href = '/login';
    }
  }, [isAuthenticated, navigate]);

  // Success state
  if (success) {
    return (
      <Container size="sm" py="xl">
        <Paper shadow="md" p="xl" radius="md" withBorder>
          <Stack align="center" gap="lg">
            <ThemeIcon size={80} radius="xl" color="green" variant="light">
              <IconCheck size={48} />
            </ThemeIcon>

            <Title order={2} ta="center">
              Email Confirmed!
            </Title>

            <Text c="dimmed" ta="center" maw={400}>
              Your email address has been verified. You now have full access to MentionedMe.
            </Text>

            {isRedirecting && (
              <Stack align="center" gap="xs">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">
                  Redirecting to your dashboard...
                </Text>
              </Stack>
            )}

            <Button
              leftSection={<IconLogin size={18} />}
              onClick={handleContinue}
              size="md"
            >
              Continue to Dashboard
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // Error state
  return (
    <Container size="sm" py="xl">
      <Paper shadow="md" p="xl" radius="md" withBorder>
        <Stack align="center" gap="lg">
          <ThemeIcon size={80} radius="xl" color="red" variant="light">
            <IconAlertCircle size={48} />
          </ThemeIcon>

          <Title order={2} ta="center">
            Confirmation Failed
          </Title>

          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            w="100%"
          >
            {errorMessage ?? 'An error occurred while confirming your email.'}
          </Alert>

          <Text c="dimmed" ta="center" maw={400}>
            If you continue to have issues, please contact support.
          </Text>

          <Button
            variant="outline"
            onClick={handleTryAgain}
            size="md"
          >
            Request New Confirmation Email
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
};

export default EmailConfirmedPage;
